// Libraries
import { useEffect } from 'react';

// Hooks
import { useAppDispatch, useAppSelector } from './app/hooks';

// Features
import {
    getTokenAsync,
    selectApiToken,
    selectMenuStatus,
    selectPlayersListStatus,
    selectModalContent,
    selectSplashMessage,
} from './features/app/appSlice';
import { selectSession } from './features/session/sessionSlice';
import { selectUser } from './features/user/userSlice';

// Components
import Lobby from './components/templates/Lobby/Lobby';
import Login from './components/templates/Login/Login';
import MaintenanceMode from './components/templates/StateScreen/MaintenanceMode';
import Session from './components/templates/Session/Session';

import Chat from './components/organisms/Chat/Chat';
import Header from './components/organisms/Header/Header';
import PlayersList from './components/organisms/PlayersList/PlayersList';
import Settings from './components/organisms/Settings/Settings';
import Modal from './components/organisms/Modal/Modal';

import VideoStream from './components/molecules/VideoStream/VideoStream';

import Splash from './components/atoms/Splash/Splash';

// Styles
import styles from './App.module.css';

/**
 * @function App
 * Top-level application component
 *
 * @example
 * return (
 *     <App />
 * )
 */
function App() {
    const dispatch = useAppDispatch();
    const apiToken = useAppSelector(selectApiToken);
    const user = useAppSelector(selectUser);
    const session = useAppSelector(selectSession);
    const menuOpen = useAppSelector(selectMenuStatus);
    const playersListOpen = useAppSelector(selectPlayersListStatus);
    const modalContent = useAppSelector(selectModalContent);
    const splashMessage = useAppSelector(selectSplashMessage);

    // If an API token is not set, attempt to fetch one
    useEffect(() => {
        if (apiToken === null) {
            dispatch(getTokenAsync());
        }
    }, [apiToken]);

    function renderTemplate() {
        if (process.env.REACT_APP_MAINTENANCE_MODE === 'true') {
            return <MaintenanceMode />;
        }

        if (apiToken === null) {
            return <Login />;
        }

        if (session === null || session === undefined) {
            return <Lobby />;
        }

        return <Session />;
    }

    return (
        <div className={styles.container}>
            <Header />
            {renderTemplate()}
            {menuOpen && <Settings />}
            {session !== null && session.session.is_live_stream && (
                <VideoStream />
            )}
            {session !== null && playersListOpen && <PlayersList />}
            {session !== null && user.chat_on && <Chat />}
            {modalContent !== '' && <Modal />}
            {splashMessage !== '' && <Splash />}
        </div>
    );
}

// Exports
export default App;
